import React, {useEffect, useState} from 'react';
import Modal from "react-modal";
import axios from "axios";
import UserService from "../../../../../services/UserService";
import {Container, Row} from "react-bootstrap";
import {useTranslation} from "react-i18next";
import TitleAndSelectBox from "../../../../common/dataView/TitleAndSelectBox";
import TitleAndInputBox from "../../../../common/dataView/TitleAndInputBox";
import DataCompareService from "../../../../../services/DataCompareService";
import styled from "styled-components";
import BoothService from "../../axiosServices/BoothService";
import _ from "lodash";
import {toast} from "react-toastify";
import ToastAlertView from "../../../../common/alert/ToastAlertView";
import ModalCloseButton from "../../../../common/modal/ModalCloseButton";
import useAxios from "../../../../../customHooks/useAxios";

const modalStyle = {
    overlay: {
        backgroundColor: 'rgba(0, 0, 0, 0.5)'
    },
    content: {
        color: 'black',
        width: '500px',
        height: '410px',
        margin: 'auto'
    }
};

const SaveButton = styled.button`
  width: 100%;
  padding: 5px;
  background: none;
  border: 1px solid #fc7242;
  border-radius: 5px;
  color: #fc7242;
  transition: all ease-in-out 0.2s;
  
  &:hover {
    background-color: #fbe1d5;
  }
  
  &:focus {
    outline: none;
  }
`;

const SaveButtonDisabled = styled.button`
  width: 100%;
  padding: 5px;
  background: none;
  border: 1px solid #757575;
  border-radius: 5px;
  color: #757575;

  &:focus {
    outline: none;
  }
`;

const requestBodyInit = {
    purchaseDate : "",
    boothUserId : "",
    boothWarrantyId : "noneId"
};

const isValidMapInit = {
    purchaseDate : false,
    boothUserId : false,
    boothWarrantyId : true
};

const BoothSalesRegisterModal = ({ isOpen, setIsOpen, onRefresh, groupId, boothId }) => {

    const { t, i18n } = useTranslation();

    /** New : axios hook */
    const { isAxiosLoading, axiosCall } = useAxios();

    /* customerList : dependent on groupId */
    const [customerList, setCustomerList] = useState([]);
    /* warrantyList : dependent on groupId */
    const [warrantyList, setWarrantyList] = useState([]);

    /* requestBody */
    const [requestBody, setRequestBody] = useState(requestBodyInit);

    /* isValid */
    const [isValidMap, setIsValidMap] = useState(isValidMapInit);
    const [isValid, setIsValid] = useState(false);


    /* fetch customerList */
    const fetchCustomerList = async () => {
        const res = await axiosCall(
            "GET",
            '/user/type/master/group/:1',
            [groupId],
            {},
            null,
            {},
            "default"
        );
        if (res.data['error_code'] === "GET_EMPTY_DATA") {
            setCustomerList([]);
        } else {
            handleCustomerIdChange(res.data.item.userList[0]['userId']);
            setCustomerList(res.data.item.userList);
        }
    };

    /* fetch warrantyList */
    const fetchWarrantyList = async () => {
        const res = await axiosCall(
            "GET",
            '/warranty',
            [],
            {},
            null,
            {},
            "default"
        );
        if (res.data['error_code'] === "GET_EMPTY_DATA") {
            let newWarrantyList = [];
            newWarrantyList.unshift({
                "warrantyId" : "noneId",
                "warrantyName" : "None",
                "warrantyName1" : t("common.none"),
                "warrantyName2" : t("common.none"),
                "warrantyName3" : t("common.none"),
                "warrantyName4" : t("common.none"),
                "warrantyName5" : t("common.none")
            });
            setWarrantyList(newWarrantyList);
        } else {
            let newWarrantyList = res.data.item.warrantyList.filter(warranty => {
                return (warranty['warrantyGroupId'] === groupId);
            });
            newWarrantyList.unshift({
                "warrantyId" : "noneId",
                "warrantyName" : "None",
                "warrantyName1" : t("common.none"),
                "warrantyName2" : t("common.none"),
                "warrantyName3" : t("common.none"),
                "warrantyName4" : t("common.none"),
                "warrantyName5" : t("common.none")
            });
            setWarrantyList(newWarrantyList);
        }
    };

    /* register booth sales */
    const registerBoothSales = async () => {
        let newRequestBody = _.cloneDeep(requestBody);
        if (newRequestBody['boothWarrantyId'] === "noneId") {
            delete newRequestBody['boothWarrantyId'];
        }
        const onSuccess = () => {
            toast.success(<ToastAlertView message={t("message.saved")} />);
            onRefresh();
            setIsOpen(false);
        };
        const onFailure = (e) => {
            console.log(e);
            toast.error(<ToastAlertView message={t("message.failed")} />);
        };
        BoothService.registerBoothSalesInfo(boothId, newRequestBody, onSuccess, onFailure);
    };

    /* utils */
    const updateRequestBody = (key, value) => {
        setRequestBody(prevObj => {
            let newObj = {...prevObj};
            newObj[key] = value;
            return newObj;
        });
    };

    const updateIsValidMap = (key, value) => {
        setIsValidMap(prevObj => {
            let newObj = {...prevObj};
            newObj[key] = value;
            return newObj;
        });
    };

    /* change handler */
    const handleCustomerIdChange = (value) => {
        updateRequestBody("boothUserId", value);
        if (value === "" | value == null) {
            updateIsValidMap("boothUserId", false);
        } else {
            updateIsValidMap("boothUserId", true);
        }
    };

    const handleWarrantyIdChange = (value) => {
        updateRequestBody("boothWarrantyId", value);
    };

    const handlePurchaseDateInput = (value) => {
        updateRequestBody("purchaseDate", value);
        if (value === "" | value == null) {
            updateIsValidMap("purchaseDate", false);
        } else {
            updateIsValidMap("purchaseDate", true);
        }
    };

    /* effects */
    useEffect(() => {
        if (groupId != null) {
            fetchCustomerList();
            fetchWarrantyList();
            setRequestBody(requestBodyInit);
            setIsValidMap(isValidMapInit);
        }
    }, [isOpen]);

    useEffect(() => {
        setIsValid(DataCompareService.checkIsAllTrue(isValidMap));
    }, [isValidMap]);

    return (
        <Modal
            style={modalStyle}
            isOpen={isOpen}
            onRequestClose={() => {
                setIsOpen(false);
            }}
        >
            <Container fluid>
                <Row>
                    <ModalCloseButton
                        title={t("menu.booth.detail.sales.register.title")}
                        setIsOpen={setIsOpen}
                    />
                </Row>
                {/* booth owner user */}
                <Row>
                    <TitleAndSelectBox
                        title={t("menu.booth.detail.sales.boothUser")}
                        innerValue={requestBody["boothUserId"]}
                        valueList={customerList}
                        valueSelector={"userId"}
                        viewSelector={"userName"}
                        onChange={handleCustomerIdChange}
                    />
                </Row>
                {/* purchase date */}
                <Row
                    style={{
                        marginTop: '10px'
                    }}
                >
                    <TitleAndInputBox
                        title={t("menu.booth.detail.sales.purchaseDate")}
                        value={requestBody["purchaseDate"]}
                        onChange={handlePurchaseDateInput}
                        isForEdit={true}
                        type={"date"}
                        isValid={isValidMap["purchaseDate"]}
                        isEnabled={true}
                    />
                </Row>
                {/* booth warranty */}
                <Row
                    style={{
                        marginTop: '10px'
                    }}
                >
                    <TitleAndSelectBox
                        title={t("menu.booth.detail.sales.boothWarrantyInfo.detail.title")}
                        innerValue={requestBody["boothWarrantyId"]}
                        valueList={warrantyList}
                        valueSelector={"warrantyId"}
                        viewSelector={t("language.dataKey.warranty.name")}
                        onChange={handleWarrantyIdChange}
                    />
                </Row>
                <Row
                    style={{
                        marginTop: '40px'
                    }}
                >
                    {
                        isValid ?
                            <SaveButton
                                onClick={() => registerBoothSales()}
                            >
                                {t("button.save")}
                            </SaveButton>
                            :
                            <SaveButtonDisabled>
                                {t("button.save")}
                            </SaveButtonDisabled>
                    }
                </Row>
            </Container>
        </Modal>
    );
};

export default BoothSalesRegisterModal;